import { memo } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import Button from '../../ui/Button';
import { formatTimestamp } from './semantics';
import type { CompanyNewsAnalysis, DaysBackOption } from './types';

interface NewsSummaryHeaderProps {
  companyName: string;
  data: CompanyNewsAnalysis | null;
  daysBack: DaysBackOption;
  onDaysBackChange: (value: DaysBackOption) => void;
  onRefresh: () => void;
  isLoading: boolean;
  isRefreshing: boolean;
  fetchedAt: number | null;
}

const DAYS_BACK_OPTIONS: DaysBackOption[] = [7, 30, 90];

function NewsSummaryHeaderBase({
  companyName,
  data,
  daysBack,
  onDaysBackChange,
  onRefresh,
  isLoading,
  isRefreshing,
  fetchedAt,
}: NewsSummaryHeaderProps) {
  const updatedAt = data?.news_batch?.fetched_at ?? fetchedAt;

  return (
    <div className="sticky top-0 z-30 border-b border-neutral-200 bg-white/95 px-6 py-4 backdrop-blur supports-[backdrop-filter]:bg-white/80">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* left: company name + subtle subtitle + timestamp */}
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2.5">
            <h2 className="truncate text-lg font-bold leading-tight text-neutral-900">
              {companyName}
            </h2>
          </div>

          {!isLoading && updatedAt && (
            <span className="text-[11px] text-neutral-400">
              Updated {formatTimestamp(updatedAt)}
            </span>
          )}
        </div>

        {/* right: time range + refresh */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5 rounded-lg border border-neutral-200 bg-neutral-50 p-0.5">
            {DAYS_BACK_OPTIONS.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => onDaysBackChange(opt)}
                className={`rounded-md px-2.5 py-1 text-xs font-semibold transition ${
                  daysBack === opt
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-neutral-500 hover:text-neutral-800'
                }`}
                aria-pressed={daysBack === opt}
              >
                {opt}d
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={isLoading || isRefreshing}
            aria-label="Refresh analysis"
          >
            {isRefreshing || isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh
          </Button>
        </div>
      </div>
    </div>
  );
}

export const NewsSummaryHeader = memo(NewsSummaryHeaderBase);
